import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Bot, Bell, PawPrint, ChevronDown, ChevronUp, LogOut, Plus, CheckCircle2, ArrowLeft, Sparkles, Trash2, AlertTriangle, Loader2, Pencil, Ticket, Copy, Check, Camera, X as XIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { BREEDS, AGE_OPTIONS } from '../constants/dogOptions';
import { ageFromBirthday } from '../utils/age';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useBox } from '../context/BoxContext';
import { TedsWheelCard } from '../components/TedsWheelCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderCard = ({ order }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`order-${order.id}`}>
    <div className="flex items-center justify-between mb-2">
      <div>
        <p className="font-bold text-[#222] text-sm">{order.box_size} Sample Box</p>
        <p className="text-xs text-[#999]">
          {new Date(order.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
        </p>
      </div>
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
        order.processed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
      }`}>
        {order.processed ? 'Processed' : 'Pending'}
      </span>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {order.selected_products.map((pid, i) => (
        <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-[#666] font-medium">{pid}</span>
      ))}
    </div>
    {order.promo_code_used && (
      <p className="text-xs text-[#0FA49C] mt-2 font-medium">Promo: {order.promo_code_used}</p>
    )}
  </div>
);

const ScanCard = ({ scan, products }) => {
  const [expanded, setExpanded] = useState(false);
  const { addProduct, isProductSelected, isFull } = useBox();
  const bag = scan.scanned_bag || {};

  const handleAdd = (rec) => {
    const product = products.find(p => p.id === rec.product_id);
    if (product && !isFull && !isProductSelected(product.id)) {
      addProduct(product);
      toast.success(`${product.name} added to your box!`);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`scan-${scan.id}`}>
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Bot className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-bold text-[#222] text-sm">{bag.brand} — {bag.product_name}</p>
            <p className="text-xs text-[#999]">
              {new Date(scan.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              {' · '}{scan.recommended_products?.length || 0} matches
              {bag.protein_source && <span> · {bag.protein_source}</span>}
            </p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#999]" /> : <ChevronDown className="w-4 h-4 text-[#999]" />}
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
          {/* Scanned bag details */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {bag.type && (
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-[10px] text-[#999] uppercase font-semibold">Type</p>
                <p className="text-xs font-medium text-[#333] capitalize">{bag.type.replace(/_/g, ' ')}</p>
              </div>
            )}
            {bag.lifestage && (
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-[10px] text-[#999] uppercase font-semibold">Lifestage</p>
                <p className="text-xs font-medium text-[#333] capitalize">{bag.lifestage}</p>
              </div>
            )}
            {bag.breed_size && (
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-[10px] text-[#999] uppercase font-semibold">Breed Size</p>
                <p className="text-xs font-medium text-[#333] capitalize">{bag.breed_size}</p>
              </div>
            )}
            {bag.protein_source && (
              <div className="bg-gray-50 rounded-lg p-2 col-span-2 sm:col-span-3">
                <p className="text-[10px] text-[#999] uppercase font-semibold">Protein Source</p>
                <p className="text-xs font-medium text-[#333]">{bag.protein_source}</p>
              </div>
            )}
            {bag.special_needs && (
              <div className="bg-gray-50 rounded-lg p-2 col-span-2 sm:col-span-3">
                <p className="text-[10px] text-[#999] uppercase font-semibold">Special Needs</p>
                <p className="text-xs font-medium text-[#333]">{bag.special_needs}</p>
              </div>
            )}
            {bag.ingredients && (
              <div className="bg-gray-50 rounded-lg p-2 col-span-2 sm:col-span-3">
                <p className="text-[10px] text-[#999] uppercase font-semibold">Ingredients</p>
                <p className="text-xs text-[#333] leading-relaxed">{bag.ingredients}</p>
              </div>
            )}
          </div>

          {/* Overall explanation */}
          {scan.explanation && (
            <div className="bg-[#0FA49C]/5 rounded-xl p-3">
              <p className="text-xs font-bold text-[#0FA49C] uppercase tracking-wide mb-1">Ted AI Summary</p>
              <p className="text-xs text-[#444] leading-relaxed">{scan.explanation}</p>
            </div>
          )}

          {/* Recommended products */}
          <div className="space-y-2">
            <p className="text-xs font-bold text-[#666] uppercase tracking-wide">Recommended Products</p>
            {scan.recommended_products?.map((rec, i) => {
              const inBox = isProductSelected(rec.product_id);
              return (
                <div key={i} className="bg-gray-50 rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-[#0FA49C] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {rec.match_score}%
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#222]">{rec.product_name}</p>
                        <p className="text-[10px] text-[#0FA49C] font-semibold uppercase">{rec.product_id}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleAdd(rec); }}
                      disabled={isFull || inBox}
                      className={`rounded-lg text-xs h-8 ${
                        inBox
                          ? 'bg-green-100 text-green-700 hover:bg-green-100'
                          : 'bg-[#0FA49C] hover:bg-[#0d8f88] text-white'
                      }`}
                      data-testid={`add-scan-product-${rec.product_id}`}
                    >
                      {inBox ? (
                        <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Added</>
                      ) : (
                        <><Plus className="w-3.5 h-3.5 mr-1" /> Add Sample</>
                      )}
                    </Button>
                  </div>
                  {rec.why_selected && (
                    <div className="bg-[#0FA49C]/5 rounded-lg p-2">
                      <p className="text-[10px] font-bold text-[#0FA49C] uppercase">Why Selected</p>
                      <p className="text-xs text-[#444] leading-relaxed">{rec.why_selected}</p>
                    </div>
                  )}
                  {rec.how_its_better && (
                    <div className="bg-green-50 rounded-lg p-2">
                      <p className="text-[10px] font-bold text-green-700 uppercase">How It's Better</p>
                      <p className="text-xs text-[#444] leading-relaxed">{rec.how_its_better}</p>
                    </div>
                  )}
                  {rec.why_upgrade && (
                    <div className="bg-amber-50 rounded-lg p-2">
                      <p className="text-[10px] font-bold text-amber-700 uppercase">Why Upgrade</p>
                      <p className="text-xs text-[#444] leading-relaxed">{rec.why_upgrade}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const AlertCard = ({ alert }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`alert-${alert.id}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="font-bold text-[#222] text-sm">{alert.brand} — {alert.product_name}</p>
        <p className="text-xs text-[#999]">
          {new Date(alert.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          {alert.current_price != null && ` · You pay £${Number(alert.current_price).toFixed(2)}`}
        </p>
      </div>
      {alert.cheapest_price && (
        <div className="text-right">
          <p className="text-sm font-bold text-green-600">£{Number(alert.cheapest_price).toFixed(2)}</p>
          <p className="text-xs text-[#999]">{alert.cheapest_site}</p>
        </div>
      )}
    </div>
    {alert.summary && (
      <p className="text-xs text-[#666] mt-2 bg-gray-50 rounded-lg p-2">{alert.summary}</p>
    )}
  </div>
);

const DogProfileCard = ({ profile }) => {
  const [expanded, setExpanded] = useState(false);
  const dog = profile.dog_profile || {};
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`profile-${profile.id}`}>
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
            <PawPrint className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="font-bold text-[#222] text-sm">{dog.dog_name}</p>
            <p className="text-xs text-[#999]">
              {dog.breed} · {dog.age}
              {dog.weight && ` · ${dog.weight}`}
              {' · '}{profile.recommended_products?.length || 0} matches
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#999]">
            {new Date(profile.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-[#999]" /> : <ChevronDown className="w-4 h-4 text-[#999]" />}
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
          {/* Dog details */}
          <div className="grid grid-cols-2 gap-2">
            {dog.activity_level && (
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-[10px] text-[#999] uppercase font-semibold">Activity</p>
                <p className="text-xs font-medium text-[#333] capitalize">{dog.activity_level}</p>
              </div>
            )}
            {dog.neutered != null && (
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-[10px] text-[#999] uppercase font-semibold">Neutered</p>
                <p className="text-xs font-medium text-[#333]">{dog.neutered ? 'Yes' : 'No'}</p>
              </div>
            )}
            {dog.sensitivities && (
              <div className="bg-gray-50 rounded-lg p-2 col-span-2">
                <p className="text-[10px] text-[#999] uppercase font-semibold">Sensitivities</p>
                <p className="text-xs font-medium text-[#333]">{dog.sensitivities}</p>
              </div>
            )}
            {dog.current_food && (
              <div className="bg-gray-50 rounded-lg p-2 col-span-2">
                <p className="text-[10px] text-[#999] uppercase font-semibold">Current Food</p>
                <p className="text-xs font-medium text-[#333]">{dog.current_food}</p>
              </div>
            )}
          </div>

          {/* Summary */}
          {profile.summary && (
            <p className="text-xs text-[#666] bg-[#0FA49C]/5 rounded-lg p-2.5">{profile.summary}</p>
          )}

          {/* Recommended products */}
          {profile.recommended_products?.map((rec, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2.5">
              <div className="w-7 h-7 rounded-full bg-purple-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                {rec.match_score}%
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[#222] truncate">{rec.product_name}</p>
                {rec.why_selected && <p className="text-xs text-[#666] line-clamp-2">{rec.why_selected}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DOG_AVATARS = ['#0FA49C', '#a0da39', '#a855f7', '#f59e0b', '#ec4899', '#3b82f6'];

const DogTile = ({ dog, onOpen, colorIdx }) => {
  const color = DOG_AVATARS[colorIdx % DOG_AVATARS.length];
  const totalLinked = dog.orders.length + dog.scans.length + dog.alerts.length;
  const avatarUrl = dog.profile?.dog_profile?.avatar_url;
  return (
    <button
      onClick={() => onOpen(dog)}
      className="group relative bg-white rounded-2xl border-2 border-gray-200 hover:border-[#0FA49C] hover:shadow-lg transition-all duration-200 p-5 text-left overflow-hidden"
      data-testid={`dog-tile-${dog.key}`}
    >
      <div className="absolute top-0 right-0 w-20 h-20 opacity-10 -mr-4 -mt-4" style={{ background: color, borderRadius: '50%' }} />
      <div className="flex items-center gap-3 mb-4">
        {avatarUrl ? (
          <img
            src={`${BACKEND_URL}${avatarUrl}`}
            alt={dog.name}
            className="w-12 h-12 rounded-2xl object-cover flex-shrink-0 shadow-sm border-2 border-white"
            data-testid={`dog-tile-${dog.key}-avatar`}
          />
        ) : (
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm"
            style={{ background: color }}
          >
            <PawPrint className="w-6 h-6 text-white" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-extrabold text-[#222] truncate">{dog.name}</p>
          {dog.profile?.dog_profile?.breed && (
            <p className="text-xs text-[#666] truncate">
              {dog.profile.dog_profile.breed}
              {dog.profile.dog_profile.age && ` · ${dog.profile.dog_profile.age}`}
            </p>
          )}
          {!dog.profile && (
            <p className="text-xs text-[#999] italic">No profile yet</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="bg-gray-50 rounded-lg py-2 text-center">
          <Package className="w-3.5 h-3.5 text-[#0FA49C] mx-auto mb-0.5" />
          <p className="text-sm font-extrabold text-[#222] leading-tight">{dog.orders.length}</p>
          <p className="text-[10px] text-[#999] uppercase font-semibold">Orders</p>
        </div>
        <div className="bg-gray-50 rounded-lg py-2 text-center">
          <Bot className="w-3.5 h-3.5 text-blue-500 mx-auto mb-0.5" />
          <p className="text-sm font-extrabold text-[#222] leading-tight">{dog.scans.length}</p>
          <p className="text-[10px] text-[#999] uppercase font-semibold">Scans</p>
        </div>
        <div className="bg-gray-50 rounded-lg py-2 text-center">
          <Bell className="w-3.5 h-3.5 text-amber-500 mx-auto mb-0.5" />
          <p className="text-sm font-extrabold text-[#222] leading-tight">{dog.alerts.length}</p>
          <p className="text-[10px] text-[#999] uppercase font-semibold">Alerts</p>
        </div>
      </div>

      {totalLinked === 0 && !dog.profile && (
        <p className="mt-3 text-xs text-[#999] italic">Nothing linked yet — start a sample box or Ted AI scan.</p>
      )}
    </button>
  );
};

const DogDetail = ({ dog, products, onBack, onProfileDeleted, onProfileUpdated }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editBreed, setEditBreed] = useState('');
  const [editAge, setEditAge] = useState('');
  const [editBirthday, setEditBirthday] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);

  const avatarUrl = dog.profile?.dog_profile?.avatar_url;

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!dog.profile?.id) {
      toast.error('Save the dog first before adding a photo');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5 MB)');
      return;
    }
    setUploadingAvatar(true);
    try {
      const token = localStorage.getItem('customer_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const form = new FormData();
      form.append('file', file);
      await axios.post(
        `${API}/account/dog-profiles/${dog.profile.id}/avatar`,
        form,
        { headers },
      );
      toast.success(`Photo saved for ${dog.name}`);
      onProfileUpdated?.(dog.name);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not upload photo');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarDelete = async () => {
    if (!dog.profile?.id || !avatarUrl) return;
    setUploadingAvatar(true);
    try {
      const token = localStorage.getItem('customer_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/account/dog-profiles/${dog.profile.id}/avatar`, { headers });
      toast.success('Photo removed');
      onProfileUpdated?.(dog.name);
    } catch (err) {
      toast.error('Could not remove photo');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const openEdit = () => {
    const p = dog.profile?.dog_profile || {};
    setEditName(p.dog_name || dog.name || '');
    setEditBreed(p.breed || '');
    setEditAge(p.age || '');
    setEditBirthday(p.birthday || '');
    setEditOpen(true);
  };

  const handleSave = async () => {
    const trimmedName = editName.trim();
    if (!trimmedName) {
      toast.error('Dog name is required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('customer_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // If a birthday is set, derive age from it so the "age" field is always
      // current and we never show a stale "6 months" on an older record.
      const derivedAge = editBirthday ? ageFromBirthday(editBirthday) : editAge.trim();
      const payload = {
        dog_name: trimmedName,
        breed: editBreed.trim() || null,
        age: derivedAge || null,
        birthday: editBirthday || null,
      };
      if (dog.profile?.id) {
        // Update existing
        await axios.patch(
          `${API}/account/dog-profiles/${dog.profile.id}`,
          payload,
          { headers }
        );
      } else {
        // Create a minimal profile so the dog shows up as a saved dog
        await axios.post(
          `${API}/account/dog-profiles`,
          payload,
          { headers }
        );
      }
      toast.success(`${trimmedName}'s profile saved`);
      setEditOpen(false);
      onProfileUpdated?.(trimmedName);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!dog.profile?.id) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('customer_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/account/dog-profiles/${dog.profile.id}`, { headers });
      toast.success(`${dog.name}'s profile deleted`);
      setConfirmOpen(false);
      onProfileDeleted?.(dog);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete profile');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid={`dog-detail-${dog.key}`}>
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0FA49C] hover:underline mb-4"
        data-testid="dog-detail-back"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to My Dogs
      </button>

      {/* Dog header */}
      <div className="bg-gradient-to-br from-[#0FA49C] to-[#0d8f88] rounded-2xl p-5 text-white mb-5">
        <div className="flex items-center gap-4">
          <div className="relative flex-shrink-0 group">
            {avatarUrl ? (
              <img
                src={`${BACKEND_URL}${avatarUrl}`}
                alt={dog.name}
                className="w-16 h-16 rounded-2xl object-cover border-2 border-white/40 shadow-md"
                data-testid="dog-detail-avatar"
              />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <PawPrint className="w-7 h-7" />
              </div>
            )}
            {dog.profile?.id && (
              <>
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white text-[#0FA49C] shadow flex items-center justify-center hover:bg-[#a0da39] hover:text-[#1f6d62] transition-colors disabled:opacity-60"
                  title={avatarUrl ? 'Change photo' : 'Add a photo'}
                  data-testid="dog-avatar-upload-btn"
                >
                  {uploadingAvatar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                </button>
                {avatarUrl && !uploadingAvatar && (
                  <button
                    type="button"
                    onClick={handleAvatarDelete}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white shadow flex items-center justify-center transition-colors"
                    title="Remove photo"
                    data-testid="dog-avatar-delete-btn"
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                )}
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleAvatarChange}
                  className="hidden"
                  data-testid="dog-avatar-file-input"
                />
              </>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider opacity-80 font-semibold">My Dog</p>
            <h2 className="text-2xl font-extrabold truncate">{dog.name}</h2>
            {dog.profile?.dog_profile && (
              <p className="text-sm text-white/85 truncate">
                {[dog.profile.dog_profile.breed, dog.profile.dog_profile.age, dog.profile.dog_profile.weight]
                  .filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={openEdit}
              className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/25 border border-white/20 text-white flex items-center justify-center transition-colors"
              title={dog.profile ? 'Edit dog details' : 'Add dog details'}
              data-testid="dog-edit-btn"
            >
              <Pencil className="w-4 h-4" />
            </button>
            {dog.profile && (
              <button
                onClick={() => setConfirmOpen(true)}
                className="w-10 h-10 rounded-xl bg-white/10 hover:bg-red-500/80 border border-white/20 text-white flex items-center justify-center transition-colors"
                title="Delete this dog's profile"
                data-testid="dog-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Profile section */}
      <section className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-purple-600" />
          <h3 className="font-bold text-[#222] text-sm uppercase tracking-wide">AI Match Profile</h3>
        </div>
        {dog.profile ? (
          <DogProfileCard profile={dog.profile} />
        ) : (
          <div className="text-center py-6 bg-white rounded-xl border border-dashed border-gray-200">
            <p className="text-sm text-[#999] mb-1">No Dog Match profile yet for {dog.name}</p>
            <p className="text-xs text-[#bbb]">Run "Get Your Dog Matched" to create one</p>
          </div>
        )}
      </section>

      {/* Orders */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-[#0FA49C]" />
            <h3 className="font-bold text-[#222] text-sm uppercase tracking-wide">Sample Box Orders</h3>
          </div>
          <span className="text-xs text-[#999] font-semibold">{dog.orders.length}</span>
        </div>
        {dog.orders.length === 0 ? (
          <div className="text-center py-6 bg-white rounded-xl border border-dashed border-gray-200">
            <p className="text-sm text-[#999]">No sample box orders yet for {dog.name}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {dog.orders.map(o => <OrderCard key={o.id} order={o} />)}
          </div>
        )}
      </section>

      {/* Scans */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-blue-500" />
            <h3 className="font-bold text-[#222] text-sm uppercase tracking-wide">Ted AI Scans</h3>
          </div>
          <span className="text-xs text-[#999] font-semibold">{dog.scans.length}</span>
        </div>
        {dog.scans.length === 0 ? (
          <div className="text-center py-6 bg-white rounded-xl border border-dashed border-gray-200">
            <p className="text-sm text-[#999]">No Ted AI scans yet for {dog.name}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {dog.scans.map(s => <ScanCard key={s.id} scan={s} products={products} />)}
          </div>
        )}
      </section>

      {/* Alerts */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-500" />
            <h3 className="font-bold text-[#222] text-sm uppercase tracking-wide">Price Alerts</h3>
          </div>
          <span className="text-xs text-[#999] font-semibold">{dog.alerts.length}</span>
        </div>
        {dog.alerts.length === 0 ? (
          <div className="text-center py-6 bg-white rounded-xl border border-dashed border-gray-200">
            <p className="text-sm text-[#999]">No price alerts yet for {dog.name}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {dog.alerts.map(a => <AlertCard key={a.id} alert={a} />)}
          </div>
        )}
      </section>

      {/* Edit dialog */}
      <AnimatePresence>
        {editOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
            onClick={() => !saving && setEditOpen(false)}
            data-testid="dog-edit-overlay"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="dog-edit-dialog"
            >
              <div className="bg-[#0FA49C] p-4 text-white flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <Pencil className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-bold text-base">Edit {dog.name}</h3>
                  <p className="text-xs text-white/80">Update name, breed, or age</p>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#333] mb-1.5">Dog's Name *</label>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="e.g. Buddy"
                    data-testid="dog-edit-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#333] mb-1.5">Breed</label>
                  <select
                    value={editBreed}
                    onChange={(e) => setEditBreed(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FA49C] focus:border-transparent"
                    data-testid="dog-edit-breed"
                  >
                    <option value="">Select breed…</option>
                    {BREEDS.map(b => <option key={b} value={b}>{b}</option>)}
                    {editBreed && !BREEDS.includes(editBreed) && (
                      <option value={editBreed}>{editBreed}</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#333] mb-1.5">Age</label>
                  <div className="grid grid-cols-2 gap-2">
                    {AGE_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setEditAge(opt.value)}
                        className={`px-3 py-2 rounded-xl text-xs font-medium border-2 transition-colors ${
                          editAge === opt.value
                            ? 'border-[#0FA49C] bg-[#0FA49C]/5 text-[#0FA49C]'
                            : 'border-gray-200 text-[#666] hover:border-gray-300'
                        }`}
                        data-testid={`dog-edit-age-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {editAge && !AGE_OPTIONS.some(a => a.value === editAge) && (
                    <p className="text-[11px] text-[#999] mt-1">Current: <strong>{editAge}</strong></p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#333] mb-1.5">Birthday <span className="text-[11px] font-normal text-[#999]">(optional — overrides age)</span></label>
                  <Input
                    type="date"
                    value={editBirthday}
                    onChange={(e) => setEditBirthday(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                    disabled={saving}
                    className="h-10"
                    data-testid="dog-edit-birthday"
                  />
                  {editBirthday && ageFromBirthday(editBirthday) && (
                    <p className="text-[11px] text-[#0FA49C] font-semibold mt-1" data-testid="dog-edit-age-derived">
                      Age: {ageFromBirthday(editBirthday)}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setEditOpen(false)}
                    disabled={saving}
                    className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-[#444] hover:bg-gray-50 transition-colors disabled:opacity-50"
                    data-testid="dog-edit-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold bg-[#0FA49C] hover:bg-[#0d8f88] text-white flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
                    data-testid="dog-edit-save"
                  >
                    {saving ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirmation dialog */}
      <AnimatePresence>
        {confirmOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
            onClick={() => !deleting && setConfirmOpen(false)}
            data-testid="dog-delete-confirm-overlay"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="dog-delete-confirm"
            >
              <div className="p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-[#222] text-base mb-1">Delete {dog.name}'s profile?</h3>
                    <p className="text-sm text-[#666] leading-relaxed">
                      This removes {dog.name}'s AI Match profile. Linked sample box orders, Ted AI scans and price alerts tagged with the name <strong>{dog.name}</strong> are kept on your account.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmOpen(false)}
                    disabled={deleting}
                    className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-[#444] hover:bg-gray-50 transition-colors disabled:opacity-50"
                    data-testid="dog-delete-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
                    data-testid="dog-delete-confirm-btn"
                  >
                    {deleting ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Deleting…</>
                    ) : (
                      <><Trash2 className="w-4 h-4" /> Delete</>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================================================
// Voucher card — a single earned voucher (wheel win, signup reward, etc).
// Shows label, description, code (copyable), source pill and a "Mark as used"
// action. Used vouchers get a subdued style so active ones stand out.
// ============================================================================

const VoucherCard = ({ voucher, onMarkUsed }) => {
  const [copied, setCopied] = useState(false);
  const [marking, setMarking] = useState(false);
  const isUsed = !!voucher.used_at;

  const copy = () => {
    if (!voucher.code) return;
    navigator.clipboard.writeText(voucher.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
    toast.success('Code copied!');
  };

  const markUsed = async () => {
    if (isUsed || marking) return;
    setMarking(true);
    try {
      const token = localStorage.getItem('customer_token');
      await axios.post(
        `${API}/customer/vouchers/${voucher.id}/mark-used`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      toast.success('Marked as used');
      onMarkUsed?.();
    } catch {
      toast.error('Could not update voucher');
    } finally {
      setMarking(false);
    }
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border-2 border-dashed p-4 shadow-sm ${
        isUsed
          ? 'bg-gray-50 border-gray-200 opacity-70'
          : 'bg-gradient-to-br from-[#1f6d62] to-[#14544b] border-[#a0da39]'
      }`}
      data-testid={`voucher-${voucher.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Ticket className={`w-3.5 h-3.5 ${isUsed ? 'text-gray-400' : 'text-[#a0da39]'}`} />
            <p className={`text-[10px] uppercase tracking-[3px] font-bold ${isUsed ? 'text-gray-400' : 'text-[#a0da39]'}`}>
              {voucher.source === 'wheel' ? "Ted's Wheel prize" : 'Voucher'}
            </p>
            {isUsed && (
              <span className="text-[10px] uppercase tracking-widest bg-gray-200 text-gray-500 font-bold px-2 py-0.5 rounded-full">
                Used
              </span>
            )}
          </div>
          <p className={`text-xl font-black leading-tight ${isUsed ? 'text-gray-500 line-through' : 'text-white'}`}>
            {voucher.label}
          </p>
          {voucher.description && (
            <p className={`text-xs mt-1 ${isUsed ? 'text-gray-400' : 'text-white/80'}`}>{voucher.description}</p>
          )}
          {voucher.claim_at && !isUsed && (
            <p className="text-[10px] uppercase tracking-widest text-[#a0da39] font-bold mt-1.5" data-testid={`voucher-claim-at-${voucher.id}`}>
              Claim at {voucher.claim_at.replace(/^https?:\/\//, '')}
            </p>
          )}
        </div>
        {!isUsed && (
          <button
            type="button"
            onClick={markUsed}
            disabled={marking}
            className="text-[10px] uppercase tracking-widest text-white/60 hover:text-white font-bold px-2 py-1 disabled:opacity-40"
            data-testid={`voucher-mark-used-${voucher.id}`}
            title="Mark as used"
          >
            Mark used
          </button>
        )}
      </div>
      {voucher.code && (
        <button
          type="button"
          onClick={copy}
          disabled={isUsed}
          className={`mt-3 w-full flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-lg tracking-widest uppercase font-black transition-colors ${
            isUsed
              ? 'bg-gray-200 text-gray-500 cursor-default'
              : 'bg-[#a0da39] hover:bg-[#b4e856] text-[#1f3d2f]'
          }`}
          data-testid={`voucher-code-${voucher.id}`}
        >
          <span className="flex-1 text-center">{voucher.code}</span>
          {!isUsed && (
            <span className="text-xs font-bold uppercase tracking-widest bg-white/60 rounded-md px-2 py-1 inline-flex items-center gap-1">
              {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
            </span>
          )}
        </button>
      )}
      <p className={`text-[10px] uppercase tracking-widest font-bold mt-2 ${isUsed ? 'text-gray-400' : 'text-white/40'}`}>
        Earned {new Date(voucher.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        {isUsed && ` · Used ${new Date(voucher.used_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
      </p>
    </div>
  );
};


export const AccountPage = () => {
  const { customer, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [scans, setScans] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [dogProfiles, setDogProfiles] = useState([]);
  const [products, setProducts] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDog, setSelectedDog] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchVouchers = async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;
    try {
      const res = await axios.get(`${API}/customer/vouchers`, { headers: { Authorization: `Bearer ${token}` } });
      setVouchers(res.data || []);
    } catch (err) {
      console.error('Failed to load vouchers', err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('customer_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const [ordersRes, scansRes, alertsRes, profilesRes, productsRes, vouchersRes] = await Promise.all([
        axios.get(`${API}/account/orders`, { headers }),
        axios.get(`${API}/account/scans`, { headers }),
        axios.get(`${API}/account/price-alerts`, { headers }),
        axios.get(`${API}/account/dog-profiles`, { headers }),
        axios.get(`${API}/products`),
        axios.get(`${API}/customer/vouchers`, { headers }),
      ]);
      setOrders(ordersRes.data);
      setScans(scansRes.data);
      setAlerts(alertsRes.data);
      setDogProfiles(profilesRes.data);
      setProducts(productsRes.data);
      setVouchers(vouchersRes.data);
    } catch (err) {
      console.error('Failed to load account data', err);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'orders', label: 'Orders', icon: Package, count: orders.length },
    { id: 'dogs', label: 'My Dogs', icon: PawPrint, count: dogProfiles.length },
    { id: 'scans', label: 'Scans', icon: Bot, count: scans.length },
    { id: 'vouchers', label: 'Vouchers', icon: Ticket, count: vouchers.filter(v => !v.used_at).length },
    { id: 'alerts', label: 'Price Alerts', icon: Bell, count: alerts.length },
  ];

  // Aggregate dogs from profiles + orders + scans + alerts by dog_name (case-insensitive)
  const dogsList = useMemo(() => {
    const byKey = {};
    const ensure = (rawName) => {
      const name = (rawName || '').trim();
      const key = name ? name.toLowerCase() : '__unlabelled__';
      if (!byKey[key]) {
        byKey[key] = {
          key,
          name: name || 'Unlabelled',
          profile: null,
          orders: [],
          scans: [],
          alerts: [],
        };
      }
      return byKey[key];
    };
    dogProfiles.forEach(p => {
      const d = ensure(p.dog_profile?.dog_name);
      if (!d.profile) d.profile = p;
    });
    orders.forEach(o => ensure(o.dog_name).orders.push(o));
    scans.forEach(s => ensure(s.dog_name).scans.push(s));
    alerts.forEach(a => ensure(a.dog_name).alerts.push(a));
    return Object.values(byKey).sort((a, b) => {
      if (a.key === '__unlabelled__') return 1;
      if (b.key === '__unlabelled__') return -1;
      return a.name.localeCompare(b.name);
    });
  }, [dogProfiles, orders, scans, alerts]);

  // Keep selectedDog in sync with refetched data
  const openDogKey = selectedDog?.key;
  const activeDog = openDogKey ? dogsList.find(d => d.key === openDogKey) : null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="account-page">
      {/* Header */}
      <div className="bg-[#0FA49C] rounded-2xl p-6 text-white mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {customer?.picture ? (
              <img src={customer.picture} alt="" className="w-14 h-14 rounded-full border-2 border-white/30" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
                {customer?.name?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            <div>
              <h2 className="font-bold text-xl">{customer?.name || 'My Account'}</h2>
              <p className="text-sm text-white/80">{customer?.email}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={logout}
            className="border-white/30 text-white hover:bg-white/20 hover:text-white rounded-xl"
            data-testid="account-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-1.5" />
            Sign Out
          </Button>
        </div>
      </div>

      {/* Ted's Wheel — one-time welcome spin (or persistent won-prize card) */}
      <div className="mb-6">
        <TedsWheelCard onWin={() => { fetchVouchers(); setActiveTab('vouchers'); }} />
      </div>

      {/* Tabs */}
      <div className="flex bg-white rounded-xl border border-gray-200 p-1 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setSelectedDog(null); }}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-semibold transition-colors ${
              activeTab === tab.id
                ? 'bg-[#0FA49C] text-white shadow-sm'
                : 'text-[#666] hover:text-[#222] hover:bg-gray-50'
            }`}
            data-testid={`account-tab-${tab.id}`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
            {tab.count > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-[#666]'
              }`}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FA49C]" />
          </div>
        ) : (
          <>
            {activeTab === 'orders' && (
              orders.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm text-[#999] mb-1">No orders yet</p>
                  <p className="text-xs text-[#bbb]">Your sample box orders will appear here</p>
                </div>
              ) : orders.map(order => <OrderCard key={order.id} order={order} />)
            )}

            {activeTab === 'dogs' && (
              activeDog ? (
                <DogDetail
                  dog={activeDog}
                  products={products}
                  onBack={() => setSelectedDog(null)}
                  onProfileDeleted={() => { setSelectedDog(null); fetchData(); }}
                  onProfileUpdated={(newName) => {
                    // Name may have changed — update selectedDog.key to follow
                    if (newName) setSelectedDog({ key: newName.toLowerCase() });
                    fetchData();
                  }}
                />
              ) : dogsList.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                  <PawPrint className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm text-[#999] mb-1">No dogs yet</p>
                  <p className="text-xs text-[#bbb]">Your dogs appear once you run a match, scan a bag, or place a sample box</p>
                </div>
              ) : (
                <div>
                  <p className="text-xs text-[#666] mb-3">Tap a dog to see their linked sample boxes, scans and price alerts.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="dogs-tiles">
                    {dogsList.map((d, i) => (
                      <DogTile key={d.key} dog={d} colorIdx={i} onOpen={setSelectedDog} />
                    ))}
                  </div>
                </div>
              )
            )}

            {activeTab === 'scans' && (
              scans.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                  <Bot className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm text-[#999] mb-1">No Ted AI scans yet</p>
                  <p className="text-xs text-[#bbb]">Scan a dog food bag to see your results here</p>
                </div>
              ) : scans.map(scan => <ScanCard key={scan.id} scan={scan} products={products} />)
            )}

            {activeTab === 'vouchers' && (
              vouchers.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                  <Ticket className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm text-[#999] mb-1">No vouchers yet</p>
                  <p className="text-xs text-[#bbb]">Spin Ted&apos;s Wheel or claim a signup offer to earn vouchers</p>
                </div>
              ) : vouchers.map(v => (
                <VoucherCard key={v.id} voucher={v} onMarkUsed={fetchVouchers} />
              ))
            )}

            {activeTab === 'alerts' && (
              alerts.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                  <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm text-[#999] mb-1">No price alerts yet</p>
                  <p className="text-xs text-[#bbb]">Opt in to price alerts during a Ted AI scan</p>
                </div>
              ) : alerts.map(alert => <AlertCard key={alert.id} alert={alert} />)
            )}
          </>
        )}
      </div>
    </div>
  );
};
